/**
 * The menu's own words, in the player's language (1.04.4, the owner, 26 October 2026: "this translation slop here,
 * remove it, do english, spanish, portuguese, and french and chinese only. and do the translations for the menu items").
 *
 * A phrase is its English text: a script says `t("Register for next round")`, an element of a page carries `t` (its
 * text is the phrase) or `t-attr="title placeholder aria-label"` (those attributes are). Each language's phrasebook
 * (client/src/translations/phrases/<language>.json: the English phrase, then its words) gives the words; English is
 * the phrase itself, and a phrase a phrasebook doesn't have stays English. `<name>` in a phrase is replaced by the
 * value given for it, in every language (`t("<n> players", { n: 12 })`).
 *
 * Rules a phrase keeps (tests/src/translations104.test.ts checks them): the first argument of `t` and `N_` is one
 * string literal (never built: the phrasebook is made from the code as written), its words are a whole sentence or a
 * whole label (never half of one, put together with another in code), and nothing calls `t` at a module's top level
 * (it would run before the phrasebook is in: always English). The keys of the game's own words (`translate`, the
 * language files' keys) are as they were.
 */

let book: Readonly<Record<string, string>> = {};
let spaced = true;
let fullStop = ".";

/**
 * the phrasebook of the language picked (its words by English phrase), whether its sentences are spaced, and its full
 * stop ("。" in Chinese, "।" in Hindi: languageLook.ts `FULL_STOPS`)
 */
export function setPhrasebook(
    phrases: Readonly<Record<string, string>> | undefined,
    options: { readonly noSpace?: boolean, readonly fullStop?: string } = {}
): void {
    book = phrases ?? {};
    spaced = options.noSpace !== true;
    fullStop = options.fullStop ?? (spaced ? "." : "。");
}

/** the book's own words for a phrase (never what every object has: "toString", "__proto__") */
const wordsOf = (phrase: string): string | undefined =>
    Object.hasOwn(book, phrase) ? book[phrase] : undefined;

/**
 * a phrase in the language picked, each `<name>` replaced by its value in `values` (in one pass: a value is never
 * read for placeholders itself; a `<name>` without a value stays)
 */
export function t(phrase: string, values?: Readonly<Record<string, string | number>>): string {
    const text = wordsOf(phrase) ?? phrase;
    if (!values) return text;
    return text.replace(/<([A-Za-z_][\w]*)>/gu, (whole, name: string) =>
        Object.hasOwn(values, name) ? String(values[name]) : whole);
}

/**
 * a phrase whose words come from elsewhere (the server's, a room's name), marked so the phrasebook has it: returned as
 * it is. `tr` of the same text then finds its words
 */
export const N_ = <T extends string>(phrase: T): T => phrase;

/**
 * words that came from elsewhere (an answer of the server's, a room's name) in the language picked, when the phrasebook
 * has them (marked with N_ where they're known); as they came otherwise
 */
export const tr = (text: string): string => wordsOf(text) ?? text;

/** sentences one after another: a space between them, none in a language written without (Chinese) */
export const sentences = (...parts: ReadonlyArray<string | false | null | undefined>): string =>
    parts.filter((part): part is string => typeof part === "string" && part.length > 0).join(spaced ? " " : "");

/** whether the language picked puts spaces between its sentences (and words): false for Chinese */
export const spacedLanguage = (): boolean => spaced;

/** words ended as a sentence, if they aren't already: with the language's own full stop */
export const sentence = (text: string): string => (/[.!?…。！？।؟]$/u.test(text) ? text : `${text}${fullStop}`);

/** an element's text as a phrase: its whitespace (the page's line breaks and indents) as one space */
export const phraseOf = (text: string): string => text.replace(/\s+/gu, " ").trim();

/**
 * The page's own words, in the language picked: each element marked `t` (only text in it) gets its phrase's words,
 * and each attribute an element names in `t-attr` its own
 */
export function translatePhrases(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>("[t]").forEach(element => {
        const phrase = phraseOf(element.textContent ?? "");
        const words = wordsOf(phrase);
        if (words !== undefined) element.textContent = words;
    });
    root.querySelectorAll<HTMLElement>("[t-attr]").forEach(element => {
        for (const name of (element.getAttribute("t-attr") ?? "").split(/\s+/u)) {
            const value = name ? element.getAttribute(name) : null;
            if (value === null) continue;
            const words = wordsOf(phraseOf(value));
            if (words !== undefined) element.setAttribute(name, words);
        }
    });
}
